import { cn } from "@/lib/utils";
import { NIVEL_CONFIG } from "./comunicado-nivel";
import { ComunicadoSistemaVigenteDTO } from "@/types/comunicado-sistema.type";

interface SuperficieProps {
  comunicado: ComunicadoSistemaVigenteDTO;
  onClick?: () => void;
  className?: string;
}

/**
 * As três superfícies onde um comunicado aparece, como componentes puros de um
 * DTO. Vivem aqui — e não dentro da barra/sino/mural — porque a prévia do
 * formulário admin renderiza os *mesmos* componentes: o que o admin vê no passo
 * de revisão é literalmente o que o usuário vai ver, sem markup paralelo para
 * sair de sincronia.
 */

/** Conteúdo da barra do topo (sem os controles de dispensar/abrir). */
export function ComunicadoBarraConteudo({
  comunicado,
}: {
  comunicado: ComunicadoSistemaVigenteDTO;
}) {
  const { Icon, badge, label } = NIVEL_CONFIG[comunicado.nivel];

  return (
    <>
      <Icon className="h-4 w-4 shrink-0" />
      <span
        className={cn(
          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase",
          badge,
        )}
      >
        {label}
      </span>
      <span className="truncate text-[13px] font-medium">{comunicado.titulo}</span>
      <span className="hidden truncate text-[13px] opacity-80 sm:inline">
        — {comunicado.resumo}
      </span>
    </>
  );
}

/** Linha do popover do sino. */
export function ComunicadoSinoItem({ comunicado, onClick, className }: SuperficieProps) {
  const { borderLeft, dot } = NIVEL_CONFIG[comunicado.nivel];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full gap-3 border-l-2 px-4 py-3 text-left transition-colors hover:bg-muted/40",
        borderLeft,
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{comunicado.titulo}</p>
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{comunicado.resumo}</p>
      </div>
      <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", dot)} />
    </button>
  );
}

/** Card do mural na Central de Módulos. */
export function ComunicadoMuralCard({ comunicado, onClick, className }: SuperficieProps) {
  const { Icon, surface, borderLeft, badge, label } = NIVEL_CONFIG[comunicado.nivel];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-2.5 rounded-lg border border-l-[3px] px-3 py-2.5 text-left transition-colors",
        surface,
        borderLeft,
        className,
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-1.5 sm:items-center">
          <span className="line-clamp-2 text-[13px] font-medium sm:truncate">
            {comunicado.titulo}
          </span>
          <span
            className={cn(
              "mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase sm:mt-0",
              badge,
            )}
          >
            {label}
          </span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs opacity-90 sm:truncate">{comunicado.resumo}</p>
      </div>
    </button>
  );
}
